/**
 * =============================================================================
 * PassiveTreeEditorScene - Editor visual para el árbol de habilidades pasivas
 * =============================================================================
 * 
 * Editor drag & drop para crear y editar el árbol de pasivas.
 * Características:
 * - 6 puntos de inicio fijos (uno por raza)
 * - Crear nodos pequeños, notables y keystones
 * - Conectar nodos entre sí
 * - Zoom y pan con mouse
 * - Guardar/cargar JSON
 */

import Phaser from 'phaser';
import {
  type PassiveNode,
  type PassiveNodeType,
  type PassiveTreeData,
  type PassiveStatModifier,
  PASSIVE_NODE_SIZES,
  PASSIVE_RACE_COLORS,
  PASSIVE_NODE_BORDER_COLORS,
  RACE_STARTING_POSITIONS,
  PASSIVE_TREE_CENTER,
} from '@shared/types/passiveTree';
import { PASSIVE_TREE_DATA } from '@shared/data/passiveTreeData';
import type { CharacterRaceId } from '@shared/types/character';
import { GAME_FONT_FAMILY } from '@shared/constants';

// Tipo extendido para incluir gnomo
type ExtendedRaceId = CharacterRaceId | 'gnome';

/**
 * Estado de un nodo en el editor
 */
interface EditorNode {
  data: PassiveNode;
  circle: Phaser.GameObjects.Arc;
  border: Phaser.GameObjects.Arc;
  label?: Phaser.GameObjects.Text;
  icon?: Phaser.GameObjects.Sprite;
}

/**
 * Conexión visual entre nodos
 */
interface EditorConnection {
  fromId: string;
  toId: string;
  line: Phaser.GameObjects.Line;
}

/**
 * Callback para notificar cambios al panel HTML
 */
export type PassiveTreeEditorCallback = (data: PassiveTreeData) => void;

export class PassiveTreeEditorScene extends Phaser.Scene {
  // Callbacks estáticos para comunicación con el panel HTML
  public static onTreeChanged: PassiveTreeEditorCallback | null = null;
  public static onNodeSelected: ((node: PassiveNode | null) => void) | null = null;
  
  // Datos del árbol
  private treeData: PassiveTreeData;
  private nodes: Map<string, EditorNode> = new Map();
  private connections: EditorConnection[] = [];
  
  // Estado del editor
  private selectedNodeId: string | null = null;
  private hoveredNodeId: string | null = null;
  private isDraggingNode: boolean = false;
  private isDraggingCamera: boolean = false;
  private dragStartX: number = 0;
  private dragStartY: number = 0;
  private dragStartScrollX: number = 0;
  private dragStartScrollY: number = 0;
  
  // Modo de edición
  private currentMode: 'select' | 'connect' | 'delete' = 'select';
  private connectFromNodeId: string | null = null;
  private pendingConnectionLine: Phaser.GameObjects.Line | null = null;
  
  // Gráficos
  private connectionGraphics: Phaser.GameObjects.Graphics | null = null;
  private gridGraphics: Phaser.GameObjects.Graphics | null = null;
  private showGrid: boolean = true;
  
  // UI elements
  private tooltipContainer: Phaser.GameObjects.Container | null = null;
  private selectionRect: Phaser.GameObjects.Rectangle | null = null;
  
  // Controles de teclado
  private cursors: Phaser.Types.Input.Keyboard.CursorKeys | null = null;
  
  // Forzar centrado de la vista
  private forceCenterView: boolean = false;
  
  constructor() {
    super({ key: 'PassiveTreeEditorScene' });
    
    // Inicializar con el árbol de pasivas de shared por defecto
    this.treeData = JSON.parse(JSON.stringify(PASSIVE_TREE_DATA));
  }
  
  /**
   * Obtiene el nombre para mostrar de una raza
   */
  private getRaceDisplayName(race: ExtendedRaceId): string {
    const names: Record<ExtendedRaceId, string> = {
      dwarf: 'Enano',
      elf: 'Elfo',
      drow: 'Elfo Oscuro',
      gnome: 'Gnomo',
      human: 'Humano',
      orc: 'Orco',
    };
    return names[race] || race;
  }
  
  preload(): void {
    // Iconos de nodos clave (todas las puntas), derivados de la data (mismo set que GameScene)
    const passiveIconKeys = new Set<string>();
    for (const n of PASSIVE_TREE_DATA.nodes) if (n.icon) passiveIconKeys.add(n.icon);
    for (const key of passiveIconKeys) {
      this.load.image(key, `/assets/icon/passive/${key.replace(/^passive_/, 'keystone_')}.png`);
    }
  }
  
  create(): void {
    console.log('[PassiveTreeEditorScene] create() - Iniciando editor');
    
    // Configurar cámara SIN bounds para permitir centrado libre
    // Esto permite que el centro siempre pueda estar centrado en la pantalla
    // sin importar el nivel de zoom
    const cam = this.cameras.main;
    cam.removeBounds(); // Sin límites de cámara
    cam.setZoom(1);
    cam.centerOn(PASSIVE_TREE_CENTER.x, PASSIVE_TREE_CENTER.y);
    
    // Crear capa de grilla
    this.gridGraphics = this.add.graphics();
    this.gridGraphics.setDepth(0);
    
    // Crear capa de conexiones
    this.connectionGraphics = this.add.graphics();
    this.connectionGraphics.setDepth(1);
    
    // Crear rectángulo de selección
    this.selectionRect = this.add.rectangle(0, 0, 0, 0, 0xFFD700, 0.3);
    this.selectionRect.setStrokeStyle(2, 0xFFD700);
    this.selectionRect.setDepth(1000);
    this.selectionRect.setVisible(false);
    
    // Renderizar nodos iniciales
    this.renderAllNodes();
    this.renderAllConnections();
    this.updateGrid();
    
    // Configurar controles de teclado (flechas)
    this.cursors = this.input.keyboard!.createCursorKeys();
    
    // Configurar input
    this.setupInput();
    
    // Deshabilitar menú contextual del click derecho
    this.game.canvas.addEventListener('contextmenu', (e) => {
      e.preventDefault();
    });
    
    // Notificar estado inicial
    this.notifyTreeChanged();
  }
  
  /**
   * Configura todos los handlers de input
   */
  private setupInput(): void {
    // Click/drag en el canvas
    this.input.on('pointerdown', this.handlePointerDown, this);
    this.input.on('pointermove', this.handlePointerMove, this);
    this.input.on('pointerup', this.handlePointerUp, this);
    
    // Zoom con rueda del mouse
    this.input.on('wheel', this.handleWheel, this);
    
    // Teclas de acceso rápido
    this.input.keyboard?.on('keydown-DELETE', () => {
      if (this.selectedNodeId) {
        this.deleteNode(this.selectedNodeId);
      }
    });
    
    this.input.keyboard?.on('keydown-ESC', () => {
      this.cancelCurrentAction();
    });
  }
  
  /**
   * Maneja el evento pointerdown
   */
  private handlePointerDown(pointer: Phaser.Input.Pointer): void {
    const worldPoint = this.cameras.main.getWorldPoint(pointer.x, pointer.y);
    
    // Click izquierdo o medio = arrastrar cámara
    if (pointer.leftButtonDown() || pointer.middleButtonDown()) {
      this.isDraggingCamera = true;
      this.dragStartX = pointer.x;
      this.dragStartY = pointer.y;
      this.dragStartScrollX = this.cameras.main.scrollX;
      this.dragStartScrollY = this.cameras.main.scrollY;
      return;
    }
    
    // Click derecho = interactuar con nodos
    if (!pointer.rightButtonDown()) {
      return;
    }
    
    // Buscar si clickeamos en un nodo
    const clickedNodeId = this.findNodeAtPosition(worldPoint.x, worldPoint.y);
    
    if (this.currentMode === 'connect') {
      // Modo conexión
      if (clickedNodeId) {
        if (!this.connectFromNodeId) {
          // Primer click - seleccionar nodo origen
          this.connectFromNodeId = clickedNodeId;
          this.selectNode(clickedNodeId);
        } else if (clickedNodeId !== this.connectFromNodeId) {
          // Segundo click - crear conexión
          this.createConnection(this.connectFromNodeId, clickedNodeId);
          this.connectFromNodeId = null;
        }
      }
    } else if (this.currentMode === 'delete') {
      // Modo eliminar
      if (clickedNodeId) {
        const node = this.treeData.nodes.find((n: PassiveNode) => n.id === clickedNodeId);
        if (node && !node.isStartingPoint) {
          this.deleteNode(clickedNodeId);
        }
      }
    } else {
      // Modo selección/mover
      if (clickedNodeId) {
        this.selectNode(clickedNodeId);
        const node = this.treeData.nodes.find((n: PassiveNode) => n.id === clickedNodeId);
        if (node && !node.isStartingPoint) {
          // Solo permitir drag en nodos que no son de inicio
          this.isDraggingNode = true;
          this.dragStartX = worldPoint.x - node.x;
          this.dragStartY = worldPoint.y - node.y;
        }
      } else {
        // Click en vacío - deseleccionar
        this.selectNode(null);
      }
    }
  }
  
  /**
   * Maneja el evento pointermove
   */
  private handlePointerMove(pointer: Phaser.Input.Pointer): void {
    const worldPoint = this.cameras.main.getWorldPoint(pointer.x, pointer.y);
    
    // Arrastrar cámara (solo si NO está forzado el centrado)
    if (this.isDraggingCamera && !this.forceCenterView) {
      const deltaX = this.dragStartX - pointer.x;
      const deltaY = this.dragStartY - pointer.y;
      const cam = this.cameras.main;
      cam.setScroll(
        this.dragStartScrollX + deltaX / cam.zoom,
        this.dragStartScrollY + deltaY / cam.zoom
      );
      this.updateGrid();
      return;
    }
    
    // Arrastrar nodo
    if (this.isDraggingNode && this.selectedNodeId) {
      const node = this.treeData.nodes.find((n: PassiveNode) => n.id === this.selectedNodeId);
      if (node) {
        // Snap a grilla de 10px
        node.x = Math.round((worldPoint.x - this.dragStartX) / 10) * 10;
        node.y = Math.round((worldPoint.y - this.dragStartY) / 10) * 10;
        this.updateNodePosition(this.selectedNodeId);
        this.renderAllConnections();
      }
      return;
    }
    
    // Hover sobre nodos
    const hoveredId = this.findNodeAtPosition(worldPoint.x, worldPoint.y);
    if (hoveredId !== this.hoveredNodeId) {
      // Quitar hover anterior
      if (this.hoveredNodeId) {
        this.setNodeHover(this.hoveredNodeId, false);
      }
      // Aplicar nuevo hover
      this.hoveredNodeId = hoveredId;
      if (hoveredId) {
        this.setNodeHover(hoveredId, true);
        this.showNodeTooltip(hoveredId, pointer.x, pointer.y);
      } else {
        this.hideTooltip();
      }
    } else if (hoveredId) {
      // Actualizar posición del tooltip
      this.updateTooltipPosition(pointer.x, pointer.y);
    }
    
    // Línea de conexión pendiente
    if (this.currentMode === 'connect' && this.connectFromNodeId) {
      this.updatePendingConnectionLine(worldPoint.x, worldPoint.y);
    }
  }
  
  /**
   * Maneja el evento pointerup
   */
  private handlePointerUp(pointer: Phaser.Input.Pointer): void {
    if (this.isDraggingNode) {
      this.isDraggingNode = false;
      this.notifyTreeChanged();
    }
    this.isDraggingCamera = false;
  }
  
  /**
   * Maneja el zoom con la rueda del mouse
   * El zoom se centra en la posición actual del mouse
   */
  private handleWheel(
    pointer: Phaser.Input.Pointer,
    gameObjects: Phaser.GameObjects.GameObject[],
    deltaX: number,
    deltaY: number
  ): void {
    const cam = this.cameras.main;
    const oldZoom = cam.zoom;
    const zoomSpeed = 0.1;
    const minZoom = 0.2;
    const maxZoom = 3.0;
    
    // Calcular nuevo zoom
    let newZoom = oldZoom;
    if (deltaY < 0) {
      newZoom = Math.min(maxZoom, oldZoom + zoomSpeed);
    } else if (deltaY > 0) {
      newZoom = Math.max(minZoom, oldZoom - zoomSpeed);
    }
    
    if (newZoom === oldZoom) return;
    
    // Obtener posición actual del mouse desde el input manager (más confiable)
    const mouseX = this.input.activePointer.x;
    const mouseY = this.input.activePointer.y;
    
    if (this.forceCenterView) {
      // Si está forzado el centrado, hacer zoom centrado en el centro del árbol
      cam.setZoom(newZoom);
      cam.centerOn(PASSIVE_TREE_CENTER.x, PASSIVE_TREE_CENTER.y);
    } else {
      // Zoom centrado en la posición del mouse
      // 1. Obtener la posición del mundo donde está el mouse ANTES del zoom
      const worldPointBefore = cam.getWorldPoint(mouseX, mouseY);
      
      // 2. Aplicar el nuevo zoom
      cam.setZoom(newZoom);
      
      // 3. Obtener la posición del mundo donde está el mouse DESPUÉS del zoom
      const worldPointAfter = cam.getWorldPoint(mouseX, mouseY);
      
      // 4. Ajustar el scroll para compensar la diferencia
      cam.scrollX += worldPointBefore.x - worldPointAfter.x;
      cam.scrollY += worldPointBefore.y - worldPointAfter.y;
    }
    
    this.updateGrid();
  }
  
  /**
   * Busca un nodo en una posición dada
   */
  private findNodeAtPosition(x: number, y: number): string | null {
    // Buscar de adelante hacia atrás (los nodos más al frente primero)
    for (let i = this.treeData.nodes.length - 1; i >= 0; i--) {
      const node = this.treeData.nodes[i];
      const radius = PASSIVE_NODE_SIZES[node.type];
      const dist = Math.sqrt((x - node.x) ** 2 + (y - node.y) ** 2);
      if (dist <= radius + 5) {
        return node.id;
      }
    }
    return null;
  }
  
  /**
   * Selecciona un nodo
   */
  private selectNode(nodeId: string | null): void {
    // Quitar selección anterior
    if (this.selectedNodeId) {
      this.setNodeSelected(this.selectedNodeId, false);
    }
    
    this.selectedNodeId = nodeId;
    
    // Aplicar nueva selección
    if (nodeId) {
      this.setNodeSelected(nodeId, true);
      const node = this.treeData.nodes.find((n: PassiveNode) => n.id === nodeId);
      if (PassiveTreeEditorScene.onNodeSelected) {
        PassiveTreeEditorScene.onNodeSelected(node || null);
      }
    } else {
      if (PassiveTreeEditorScene.onNodeSelected) {
        PassiveTreeEditorScene.onNodeSelected(null);
      }
    }
  }
  
  /**
   * Actualiza el estado visual de selección de un nodo
   */
  private setNodeSelected(nodeId: string, selected: boolean): void {
    const editorNode = this.nodes.get(nodeId);
    if (!editorNode) return;
    
    if (selected) {
      editorNode.border.setStrokeStyle(3, PASSIVE_NODE_BORDER_COLORS.allocated);
      
      // Mostrar rectángulo de selección
      if (this.selectionRect) {
        const size = PASSIVE_NODE_SIZES[editorNode.data.type] + 10;
        this.selectionRect.setPosition(editorNode.data.x, editorNode.data.y);
        this.selectionRect.setSize(size * 2, size * 2);
        this.selectionRect.setVisible(true);
      }
    } else {
      editorNode.border.setStrokeStyle(2, PASSIVE_NODE_BORDER_COLORS.available);
      if (this.selectionRect) {
        this.selectionRect.setVisible(false);
      }
    }
  }
  
  /**
   * Actualiza el estado visual de hover de un nodo
   */
  private setNodeHover(nodeId: string, hovered: boolean): void {
    const editorNode = this.nodes.get(nodeId);
    if (!editorNode) return;
    
    if (hovered && nodeId !== this.selectedNodeId) {
      editorNode.border.setStrokeStyle(2, PASSIVE_NODE_BORDER_COLORS.hover);
    } else if (nodeId !== this.selectedNodeId) {
      editorNode.border.setStrokeStyle(2, PASSIVE_NODE_BORDER_COLORS.available);
    }
  }
  
  /**
   * Renderiza todos los nodos
   */
  private renderAllNodes(): void {
    // Limpiar nodos existentes
    this.nodes.forEach(editorNode => {
      editorNode.circle.destroy();
      editorNode.border.destroy();
      editorNode.label?.destroy();
      editorNode.icon?.destroy();
    });
    this.nodes.clear();
    
    // Crear nodos
    for (const nodeData of this.treeData.nodes) {
      this.createNodeGraphics(nodeData);
    }
  }
  
  /**
   * Crea los gráficos para un nodo
   */
  private createNodeGraphics(nodeData: PassiveNode): void {
    const radius = PASSIVE_NODE_SIZES[nodeData.type];
    const color = nodeData.raceAffinity 
      ? PASSIVE_RACE_COLORS[nodeData.raceAffinity]
      : PASSIVE_RACE_COLORS.neutral;
    
    // Crear círculo de fondo (borde)
    const border = this.add.circle(nodeData.x, nodeData.y, radius + 3, 0x000000);
    border.setStrokeStyle(2, PASSIVE_NODE_BORDER_COLORS.available);
    border.setDepth(10);
    
    // Crear círculo principal
    const circle = this.add.circle(nodeData.x, nodeData.y, radius, color);
    circle.setDepth(11);
    
    // Si es punto de inicio, añadir indicador especial
    if (nodeData.isStartingPoint) {
      border.setStrokeStyle(3, 0xFFD700);
    }
    
    // Label para keystones y notables
    let label: Phaser.GameObjects.Text | undefined;
    if (nodeData.type !== 'small') {
      label = this.add.text(nodeData.x, nodeData.y + radius + 8, nodeData.name, {
        fontSize: nodeData.type === 'keystone' ? '11px' : '9px',
        color: '#ffffff',
        fontFamily: GAME_FONT_FAMILY,
        stroke: '#000000',
        strokeThickness: 2,
      });
      label.setOrigin(0.5, 0);
      label.setDepth(12);
    }
    
    const editorNode: EditorNode = {
      data: nodeData,
      circle,
      border,
      label,
    };
    
    this.nodes.set(nodeData.id, editorNode);
  }
  
  /**
   * Actualiza la posición visual de un nodo
   */
  private updateNodePosition(nodeId: string): void {
    const editorNode = this.nodes.get(nodeId);
    if (!editorNode) return;
    
    const nodeData = editorNode.data;
    editorNode.circle.setPosition(nodeData.x, nodeData.y);
    editorNode.border.setPosition(nodeData.x, nodeData.y);
    
    if (editorNode.label) {
      const radius = PASSIVE_NODE_SIZES[nodeData.type];
      editorNode.label.setPosition(nodeData.x, nodeData.y + radius + 8);
    }
    
    if (this.selectedNodeId === nodeId && this.selectionRect) {
      this.selectionRect.setPosition(nodeData.x, nodeData.y);
    }
  }
  
  /**
   * Renderiza todas las conexiones
   */
  private renderAllConnections(): void {
    if (!this.connectionGraphics) return;
    
    this.connectionGraphics.clear();
    this.connectionGraphics.lineStyle(3, 0x666666, 1);
    
    // Set para evitar dibujar conexiones duplicadas
    const drawnConnections = new Set<string>();
    
    for (const node of this.treeData.nodes) {
      for (const connId of node.connections) {
        // Crear key única para la conexión (ordenada alfabéticamente)
        const key = [node.id, connId].sort().join('|');
        if (drawnConnections.has(key)) continue;
        drawnConnections.add(key);
        
        const targetNode = this.treeData.nodes.find((n: PassiveNode) => n.id === connId);
        if (!targetNode) continue;
        
        // Dibujar línea
        this.connectionGraphics.moveTo(node.x, node.y);
        this.connectionGraphics.lineTo(targetNode.x, targetNode.y);
      }
    }
    
    this.connectionGraphics.strokePath();
  }
  
  /**
   * Actualiza la grilla de fondo
   */
  private updateGrid(): void {
    if (!this.gridGraphics || !this.showGrid) {
      this.gridGraphics?.clear();
      return;
    }
    
    this.gridGraphics.clear();
    
    const cam = this.cameras.main;
    const view = cam.worldView;
    const gridSize = 50;
    
    // Calcular rango visible
    const startX = Math.floor(view.x / gridSize) * gridSize;
    const startY = Math.floor(view.y / gridSize) * gridSize;
    const endX = Math.ceil(view.right / gridSize) * gridSize;
    const endY = Math.ceil(view.bottom / gridSize) * gridSize;
    
    // Grilla menor (tenue)
    this.gridGraphics.lineStyle(1, 0x222222, 0.5);
    
    for (let x = startX; x <= endX; x += gridSize) {
      this.gridGraphics.moveTo(x, startY);
      this.gridGraphics.lineTo(x, endY);
    }
    
    for (let y = startY; y <= endY; y += gridSize) {
      this.gridGraphics.moveTo(startX, y);
      this.gridGraphics.lineTo(endX, y);
    }
    
    this.gridGraphics.strokePath();
    
    // Líneas de centro (más visibles)
    this.gridGraphics.lineStyle(2, 0x444444, 0.8);
    
    // Línea vertical del centro
    if (PASSIVE_TREE_CENTER.x >= view.x && PASSIVE_TREE_CENTER.x <= view.right) {
      this.gridGraphics.moveTo(PASSIVE_TREE_CENTER.x, startY);
      this.gridGraphics.lineTo(PASSIVE_TREE_CENTER.x, endY);
    }
    
    // Línea horizontal del centro
    if (PASSIVE_TREE_CENTER.y >= view.y && PASSIVE_TREE_CENTER.y <= view.bottom) {
      this.gridGraphics.moveTo(startX, PASSIVE_TREE_CENTER.y);
      this.gridGraphics.lineTo(endX, PASSIVE_TREE_CENTER.y);
    }
    
    this.gridGraphics.strokePath();
  }
  
  /**
   * Muestra un tooltip para un nodo
   */
  private showNodeTooltip(nodeId: string, screenX: number, screenY: number): void {
    const node = this.treeData.nodes.find((n: PassiveNode) => n.id === nodeId);
    if (!node) return;
    
    this.hideTooltip();
    
    // Crear contenedor del tooltip
    const padding = 8;
    const maxWidth = 200;
    
    // Crear textos
    const titleText = this.add.text(padding, padding, node.name, {
      fontSize: '12px',
      fontFamily: GAME_FONT_FAMILY,
      color: '#FFD700',
      fontStyle: 'bold',
    });
    titleText.setScrollFactor(0);
    
    const typeText = this.add.text(padding, padding + 16, `[${node.type.toUpperCase()}]`, {
      fontSize: '9px',
      fontFamily: GAME_FONT_FAMILY,
      color: '#888888',
    });
    typeText.setScrollFactor(0);
    
    let modifiersText: Phaser.GameObjects.Text | null = null;
    if (node.modifiers.length > 0) {
      const modText = node.modifiers.map((m: PassiveStatModifier) => {
        const sign = m.value >= 0 ? '+' : '';
        const suffix = m.isPercentage ? '%' : '';
        return `${sign}${m.value}${suffix} ${m.stat}`;
      }).join('\n');
      
      modifiersText = this.add.text(padding, padding + 32, modText, {
        fontSize: '10px',
        fontFamily: GAME_FONT_FAMILY,
        color: '#aaffaa',
      });
      modifiersText.setScrollFactor(0);
    }
    
    // Calcular tamaño del fondo
    const textWidth = Math.max(
      titleText.width,
      typeText.width,
      modifiersText?.width || 0
    );
    const textHeight = padding + 32 + (modifiersText?.height || 0) + padding;
    
    // Crear fondo
    const bg = this.add.rectangle(0, 0, textWidth + padding * 2, textHeight, 0x000000, 0.9);
    bg.setOrigin(0, 0);
    bg.setStrokeStyle(1, 0x444444);
    bg.setScrollFactor(0);
    
    // Crear contenedor
    this.tooltipContainer = this.add.container(screenX + 15, screenY + 15);
    this.tooltipContainer.setDepth(2000);
    this.tooltipContainer.setScrollFactor(0);
    this.tooltipContainer.add([bg, titleText, typeText]);
    if (modifiersText) {
      this.tooltipContainer.add(modifiersText);
    }
  }
  
  /**
   * Actualiza la posición del tooltip
   */
  private updateTooltipPosition(screenX: number, screenY: number): void {
    if (this.tooltipContainer) {
      this.tooltipContainer.setPosition(screenX + 15, screenY + 15);
    }
  }
  
  /**
   * Oculta el tooltip
   */
  private hideTooltip(): void {
    if (this.tooltipContainer) {
      this.tooltipContainer.destroy();
      this.tooltipContainer = null;
    }
  }
  
  /**
   * Actualiza la línea de conexión pendiente
   */
  private updatePendingConnectionLine(targetX: number, targetY: number): void {
    if (!this.connectFromNodeId) return;
    
    const fromNode = this.treeData.nodes.find((n: PassiveNode) => n.id === this.connectFromNodeId);
    if (!fromNode) return;
    
    if (this.pendingConnectionLine) {
      this.pendingConnectionLine.destroy();
    }
    
    this.pendingConnectionLine = this.add.line(
      0, 0,
      fromNode.x, fromNode.y,
      targetX, targetY,
      0xFFD700
    );
    this.pendingConnectionLine.setLineWidth(2);
    this.pendingConnectionLine.setOrigin(0, 0);
    this.pendingConnectionLine.setDepth(5);
  }
  
  /**
   * Cancela la acción actual
   */
  private cancelCurrentAction(): void {
    this.connectFromNodeId = null;
    if (this.pendingConnectionLine) {
      this.pendingConnectionLine.destroy();
      this.pendingConnectionLine = null;
    }
    this.selectNode(null);
  }
  
  // ==========================================================================
  // MÉTODOS PÚBLICOS - API para el panel HTML
  // ==========================================================================
  
  /**
   * Cambia el modo de edición
   */
  public setMode(mode: 'select' | 'connect' | 'delete'): void {
    this.currentMode = mode;
    this.cancelCurrentAction();
    console.log('[PassiveTreeEditor] Modo cambiado a:', mode);
  }
  
  /**
   * Crea un nuevo nodo
   */
  public createNode(type: PassiveNodeType, name: string, raceAffinity: ExtendedRaceId | null): string {
    // Generar ID único
    const id = `node_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    // Posición: cerca del centro o del nodo seleccionado
    let x = PASSIVE_TREE_CENTER.x;
    let y = PASSIVE_TREE_CENTER.y;
    
    if (this.selectedNodeId) {
      const selectedNode = this.treeData.nodes.find((n: PassiveNode) => n.id === this.selectedNodeId);
      if (selectedNode) {
        x = selectedNode.x + 60;
        y = selectedNode.y;
      }
    }
    
    const newNode: PassiveNode = {
      id,
      name: name || `Nodo ${type}`,
      description: '',
      type,
      x,
      y,
      connections: [],
      modifiers: [],
      raceAffinity,
    };
    
    this.treeData.nodes.push(newNode);
    this.createNodeGraphics(newNode);
    this.selectNode(id);
    this.notifyTreeChanged();
    
    return id;
  }
  
  /**
   * Elimina un nodo
   */
  public deleteNode(nodeId: string): boolean {
    const node = this.treeData.nodes.find((n: PassiveNode) => n.id === nodeId);
    if (!node || node.isStartingPoint) {
      console.warn('[PassiveTreeEditor] No se puede eliminar nodo de inicio');
      return false;
    }
    
    // Eliminar conexiones a este nodo
    for (const otherNode of this.treeData.nodes) {
      const connIdx = otherNode.connections.indexOf(nodeId);
      if (connIdx !== -1) {
        otherNode.connections.splice(connIdx, 1);
      }
    }
    
    // Eliminar el nodo del array
    const idx = this.treeData.nodes.findIndex((n: PassiveNode) => n.id === nodeId);
    if (idx !== -1) {
      this.treeData.nodes.splice(idx, 1);
    }
    
    // Eliminar gráficos
    const editorNode = this.nodes.get(nodeId);
    if (editorNode) {
      editorNode.circle.destroy();
      editorNode.border.destroy();
      editorNode.label?.destroy();
      editorNode.icon?.destroy();
      this.nodes.delete(nodeId);
    }
    
    // Deseleccionar si estaba seleccionado
    if (this.selectedNodeId === nodeId) {
      this.selectNode(null);
    }
    
    this.renderAllConnections();
    this.notifyTreeChanged();
    
    return true;
  }
  
  /**
   * Crea una conexión entre dos nodos
   */
  public createConnection(fromId: string, toId: string): boolean {
    const fromNode = this.treeData.nodes.find((n: PassiveNode) => n.id === fromId);
    const toNode = this.treeData.nodes.find((n: PassiveNode) => n.id === toId);
    
    if (!fromNode || !toNode) return false;
    if (fromId === toId) return false;
    if (fromNode.connections.includes(toId)) return false;
    
    // Añadir conexión bidireccional
    fromNode.connections.push(toId);
    toNode.connections.push(fromId);
    
    this.renderAllConnections();
    this.notifyTreeChanged();
    
    // Limpiar línea pendiente
    if (this.pendingConnectionLine) {
      this.pendingConnectionLine.destroy();
      this.pendingConnectionLine = null;
    }
    
    return true;
  }
  
  /**
   * Elimina una conexión entre dos nodos
   */
  public removeConnection(fromId: string, toId: string): boolean {
    const fromNode = this.treeData.nodes.find((n: PassiveNode) => n.id === fromId);
    const toNode = this.treeData.nodes.find((n: PassiveNode) => n.id === toId);
    
    if (!fromNode || !toNode) return false;
    
    const fromIdx = fromNode.connections.indexOf(toId);
    const toIdx = toNode.connections.indexOf(fromId);
    
    if (fromIdx !== -1) fromNode.connections.splice(fromIdx, 1);
    if (toIdx !== -1) toNode.connections.splice(toIdx, 1);
    
    this.renderAllConnections();
    this.notifyTreeChanged();
    
    return true;
  }
  
  /**
   * Actualiza las propiedades de un nodo
   */
  public updateNode(nodeId: string, updates: Partial<PassiveNode>): boolean {
    const node = this.treeData.nodes.find((n: PassiveNode) => n.id === nodeId);
    if (!node) return false;
    
    // No permitir cambiar isStartingPoint
    const { isStartingPoint, id, ...safeUpdates } = updates;
    
    Object.assign(node, safeUpdates);
    
    // Actualizar gráficos si cambió el tipo o posición
    if ('type' in updates || 'raceAffinity' in updates) {
      // Recrear el nodo
      const editorNode = this.nodes.get(nodeId);
      if (editorNode) {
        editorNode.circle.destroy();
        editorNode.border.destroy();
        editorNode.label?.destroy();
        editorNode.icon?.destroy();
        this.nodes.delete(nodeId);
      }
      this.createNodeGraphics(node);
    } else if ('x' in updates || 'y' in updates) {
      this.updateNodePosition(nodeId);
      this.renderAllConnections();
    }
    
    // Actualizar label si cambió el nombre
    if ('name' in updates) {
      const editorNode = this.nodes.get(nodeId);
      if (editorNode?.label) {
        editorNode.label.setText(node.name);
      }
    }
    
    this.notifyTreeChanged();
    return true;
  }
  
  /**
   * Añade un modificador a un nodo
   */
  public addModifier(nodeId: string, modifier: PassiveStatModifier): boolean {
    const node = this.treeData.nodes.find((n: PassiveNode) => n.id === nodeId);
    if (!node) return false;
    
    node.modifiers.push(modifier);
    this.notifyTreeChanged();
    return true;
  }
  
  /**
   * Elimina un modificador de un nodo
   */
  public removeModifier(nodeId: string, index: number): boolean {
    const node = this.treeData.nodes.find((n: PassiveNode) => n.id === nodeId);
    if (!node || index < 0 || index >= node.modifiers.length) return false;
    
    node.modifiers.splice(index, 1);
    this.notifyTreeChanged();
    return true;
  }
  
  /**
   * Carga un árbol desde JSON
   */
  public loadTree(data: PassiveTreeData): void {
    this.treeData = data;
    this.renderAllNodes();
    this.renderAllConnections();
    this.selectNode(null);
    
    // Centrar cámara
    this.cameras.main.centerOn(data.center.x, data.center.y);
    this.updateGrid();
    
    this.notifyTreeChanged();
    console.log('[PassiveTreeEditor] Árbol cargado:', data.nodes.length, 'nodos');
  }
  
  /**
   * Obtiene los datos actuales del árbol
   */
  public getTreeData(): PassiveTreeData {
    // Calcular bounds automáticamente
    if (this.treeData.nodes.length > 0) {
      let minX = Infinity, maxX = -Infinity;
      let minY = Infinity, maxY = -Infinity;
      
      for (const node of this.treeData.nodes) {
        minX = Math.min(minX, node.x);
        maxX = Math.max(maxX, node.x);
        minY = Math.min(minY, node.y);
        maxY = Math.max(maxY, node.y);
      }
      
      this.treeData.bounds = {
        minX: minX - 100,
        maxX: maxX + 100,
        minY: minY - 100,
        maxY: maxY + 100,
      };
    }
    
    return JSON.parse(JSON.stringify(this.treeData));
  }
  
  /**
   * Exporta el árbol a JSON string
   */
  public exportToJson(): string {
    return JSON.stringify(this.getTreeData(), null, 2);
  }
  
  /**
   * Centra la vista en el árbol
   */
  public centerView(): void {
    this.cameras.main.centerOn(PASSIVE_TREE_CENTER.x, PASSIVE_TREE_CENTER.y);
    this.cameras.main.setZoom(1);
    this.updateGrid();
  }
  
  /**
   * Alterna la visualización de la grilla
   */
  public toggleGrid(show?: boolean): void {
    this.showGrid = show ?? !this.showGrid;
    this.updateGrid();
  }
  
  /**
   * Notifica cambios al panel HTML
   */
  private notifyTreeChanged(): void {
    if (PassiveTreeEditorScene.onTreeChanged) {
      PassiveTreeEditorScene.onTreeChanged(this.getTreeData());
    }
  }
  
  update(time: number, delta: number): void {
    // Actualizar FPS en el elemento HTML
    const fpsElement = document.getElementById('passive-tree-editor-fps');
    if (fpsElement) {
      const fps = Math.round(1000 / delta);
      fpsElement.textContent = `${fps}`;
    }
    
    // Si forzar centrado está activo, centrar la vista
    if (this.forceCenterView) {
      this.cameras.main.centerOn(PASSIVE_TREE_CENTER.x, PASSIVE_TREE_CENTER.y);
      return; // No procesar movimiento de cámara si está forzado
    }
    
    // Movimiento de cámara con flechas del teclado
    if (this.cursors) {
      const cam = this.cameras.main;
      const speed = 300 * (delta / 1000); // 300 pixels por segundo
      let moved = false;
      
      if (this.cursors.left?.isDown) {
        cam.scrollX -= speed;
        moved = true;
      }
      if (this.cursors.right?.isDown) {
        cam.scrollX += speed;
        moved = true;
      }
      if (this.cursors.up?.isDown) {
        cam.scrollY -= speed;
        moved = true;
      }
      if (this.cursors.down?.isDown) {
        cam.scrollY += speed;
        moved = true;
      }
      
      if (moved) {
        this.updateGrid();
      }
    }
  }
  
  /**
   * Activa o desactiva el forzar centrado de la vista
   */
  public setForceCenterView(force: boolean): void {
    this.forceCenterView = force;
    if (force) {
      this.centerView();
    }
  }
  
  /**
   * Obtiene el estado actual de forzar centrado
   */
  public getForceCenterView(): boolean {
    return this.forceCenterView;
  }
}
